import { servicios } from "./servicio";

const SCHEDULING_STATUS_LABELS = {
  CONFLICT: "Conflicto",
  NEGATIVE_BLOCK: "Bloqueo parcial",
  INSUFFICIENT_POSITIVE_COVERAGE: "Cobertura insuficiente",
  AVAILABLE: "Disponible",
};

const SCHEDULING_STATUS_DESCRIPTIONS = {
  CONFLICT: "Conflicto con otro evento del calendario",
  NEGATIVE_BLOCK: "Bloqueo del profesional (vacacion o ausencia)",
  INSUFFICIENT_POSITIVE_COVERAGE:
    "No hay turno positivo que cubra este horario",
  AVAILABLE: "Disponible",
};

const TOLERANCE_STATUS_LABELS = {
  WITHIN: "Dentro de tolerancia",
  EXCEEDS: "Excede tolerancia",
};

const buildUrl = (url, op, parameters = {}) => {
  const queryString = new URLSearchParams(
    Object.entries(parameters).reduce((acc, [key, value]) => {
      if (value === undefined || value === null || value === "") {
        return acc;
      }
      acc[key] = value;
      return acc;
    }, {})
  ).toString();

  return queryString ? `${url}?op=${op}&${queryString}` : `${url}?op=${op}`;
};

const hhmm = (value) => {
  if (typeof value !== "string") return null;
  const match = value.match(/[T ](\d{2}:\d{2})|^(\d{2}:\d{2})/);
  return match ? match[1] ?? match[2] : null;
};

const toFiniteOrNull = (value) =>
  Number.isFinite(Number(value)) ? Number(value) : null;

const toNullableString = (value) =>
  typeof value === "string" && value.trim() !== "" ? value : null;

const isValidTimeZone = (timezone) => {
  if (!timezone) return false;
  try {
    new Intl.DateTimeFormat("en-CA", { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
};

export const getBrowserTimeZone = () => {
  const resolvedTimezone =
    typeof Intl !== "undefined"
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : null;

  return isValidTimeZone(resolvedTimezone) ? resolvedTimezone : null;
};

const normalizeConflictSummary = (raw, { includeReasons = false } = {}) => {
  const reasons = Array.isArray(raw?.reasons)
    ? raw.reasons.filter(
        (reason) => typeof reason === "string" && reason.trim() !== ""
      )
    : [];

  const conflictSummary = {
    conflicts: toFiniteOrNull(raw?.conflicts) ?? 0,
  };

  if (includeReasons) {
    conflictSummary.reasons = reasons;
  }

  return conflictSummary;
};

const isPositiveAgendaId = (value) => {
  if (value === undefined || value === null || value === "") return false;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0;
};

const toAgendaQueryParameters = ({
  idCentro,
  idcentro,
  idProfesional,
  idServicio,
  dia,
  rangeStart,
  rangeEnd,
  fechainicia,
  fechafinaliza,
  ...rest
} = {}) => ({
  ...rest,
  ...(dia ? { dia } : {}),
  ...(rangeStart || fechainicia
    ? { fechainicia: rangeStart ?? fechainicia }
    : {}),
  ...(rangeEnd || fechafinaliza
    ? { fechafinaliza: rangeEnd ?? fechafinaliza }
    : {}),
  ...(idCentro || idcentro ? { idcentro: idCentro ?? idcentro } : {}),
  ...(idProfesional ? { idProfesional } : {}),
  ...(isPositiveAgendaId(idServicio) ? { idServicio } : {}),
});

const dateTimeFormatterCache = new Map();

const getDateTimeFormatter = (timezone) => {
  const resolvedTimezone = toNullableString(timezone);
  if (!isValidTimeZone(resolvedTimezone)) return null;

  if (!dateTimeFormatterCache.has(resolvedTimezone)) {
    dateTimeFormatterCache.set(
      resolvedTimezone,
      new Intl.DateTimeFormat("en-CA", {
        timeZone: resolvedTimezone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
      })
    );
  }
  return dateTimeFormatterCache.get(resolvedTimezone);
};

const getUtcDateTimeParts = (utcValue, timezone) => {
  if (typeof utcValue !== "string" || utcValue.trim() === "") return null;
  const dateValue = new Date(utcValue);
  if (Number.isNaN(dateValue.getTime())) return null;

  const formatter = getDateTimeFormatter(timezone);
  if (!formatter) return null;

  const parts = formatter.formatToParts(dateValue);
  const getPart = (type) =>
    parts.find((part) => part.type === type)?.value ?? null;

  const year = getPart("year");
  const month = getPart("month");
  const day = getPart("day");
  const hour = getPart("hour");
  const minute = getPart("minute");

  if (!year || !month || !day || !hour || !minute) return null;

  return { year, month, day, hour, minute };
};

export const utcToLocal = (utcValue, timezone) => {
  const parts = getUtcDateTimeParts(utcValue, timezone);
  if (!parts) return null;
  const { year, month, day, hour, minute } = parts;
  return `${year}-${month}-${day}T${hour}:${minute}:00`;
};

const toAgendaSlotSummary = (slot) => {
  const statusText = getAgendaSlotStatusDescription(slot?.status);
  const toleranceText =
    slot?.toleranceStatus === "EXCEEDS"
      ? getAgendaToleranceStatusLabel(slot?.toleranceStatus)
      : "";

  return [statusText, toleranceText].filter(Boolean).join(" | ");
};

const normalizeAgendaSlot = (slot) => {
  if (!slot || typeof slot !== "object") return null;

  if (slot?.schedulingTimeError) {
    return {
      ...slot,
      citaId: null,
      isOccupied: false,
      isBookable: false,
      isDisabled: true,
      summary: slot.schedulingTimeError,
      value:
        toNullableString(slot?.value) ??
        toNullableString(slot?.startUtc) ??
        "invalid-time",
      label: toNullableString(slot?.label) ?? "Hora no disponible",
    };
  }

  const normalizedValue =
    hhmm(slot?.value) ??
    hhmm(slot?.localStartDateTime) ??
    hhmm(slot?.startUtc ? utcToLocal(slot.startUtc, slot?.timezone) : null);

  if (!normalizedValue) return null;

  const normalizedEndValue =
    hhmm(slot?.endValue) ??
    hhmm(slot?.localEndDateTime) ??
    hhmm(slot?.endUtc ? utcToLocal(slot.endUtc, slot?.timezone) : null);

  const isOccupied = isPositiveAgendaId(slot?.citaId);
  const summary = toAgendaSlotSummary(slot);

  return {
    ...slot,
    citaId: isOccupied ? Number(slot.citaId) : null,
    isOccupied,
    summary,
    value: normalizedValue,
    endValue: normalizedEndValue,
    label: normalizedEndValue
      ? `${normalizedValue} - ${normalizedEndValue}`
      : normalizedValue,
  };
};

const buildInvalidAgendaSlot = (slot, schedulingTimeError) =>
  normalizeAgendaSlot({
    ...slot,
    schedulingTimeError,
    value:
      toNullableString(slot?.id) ??
      toNullableString(slot?.startUtc) ??
      toNullableString(slot?.localStartDateTime) ??
      "invalid-time",
    label: "Hora no disponible",
  });

const getAgendaOptionIdentity = (slot) =>
  toNullableString(slot?.startUtc) ??
  toNullableString(slot?.localStartDateTime) ??
  toNullableString(slot?.value) ??
  null;

const getAgendaOptionRichness = (slot) => {
  let score = 0;

  if (slot?.citaId != null) score += 8;
  if (slot?.status) score += 4;
  if (slot?.toleranceStatus) score += 4;
  if (slot?.outOfShift === true) score += 2;
  if (slot?.isDisabled === true) score += 1;

  return score;
};

const dedupeAgendaOptions = (options = []) =>
  options.reduce((acc, option) => {
    const identity = getAgendaOptionIdentity(option);
    if (!identity) {
      acc.push(option);
      return acc;
    }

    const existingIndex = acc.findIndex(
      (current) => getAgendaOptionIdentity(current) === identity
    );

    if (existingIndex === -1) {
      acc.push(option);
      return acc;
    }

    if (
      getAgendaOptionRichness(option) >
      getAgendaOptionRichness(acc[existingIndex])
    ) {
      acc[existingIndex] = option;
    }

    return acc;
  }, []);

const compareAgendaOptionOrder = (left, right) => {
  const leftGroup = left?.outOfShift === true ? 1 : 0;
  const rightGroup = right?.outOfShift === true ? 1 : 0;

  if (leftGroup !== rightGroup) {
    return leftGroup - rightGroup;
  }

  const leftIdentity = getAgendaOptionIdentity(left) ?? left?.value ?? "";
  const rightIdentity = getAgendaOptionIdentity(right) ?? right?.value ?? "";

  return String(leftIdentity).localeCompare(String(rightIdentity));
};

export const extractAgendaTime = (slot) => {
  if (typeof slot === "string") return hhmm(slot);
  if (!slot || typeof slot !== "object") return null;
  return normalizeAgendaSlot(slot)?.value ?? null;
};

export const findAgendaHourOption = (options, timeLike) => {
  const targetValue =
    typeof timeLike === "string" ? hhmm(timeLike) : extractAgendaTime(timeLike);

  if (!targetValue || !Array.isArray(options)) return null;
  return options.find((option) => option?.value === targetValue) ?? null;
};

export const getAgendaSlotStatusLabel = (status) => {
  switch (status) {
    case "CONFLICT":
    case "NEGATIVE_BLOCK":
    case "INSUFFICIENT_POSITIVE_COVERAGE":
    case "AVAILABLE":
      return SCHEDULING_STATUS_LABELS[status];
    default:
      return typeof status === "string" && status.trim() !== ""
        ? status
        : "";
  }
};

export const getAgendaSlotStatusDescription = (status) => {
  switch (status) {
    case "CONFLICT":
    case "NEGATIVE_BLOCK":
    case "INSUFFICIENT_POSITIVE_COVERAGE":
    case "AVAILABLE":
      return SCHEDULING_STATUS_DESCRIPTIONS[status];
    default:
      return typeof status === "string" && status.trim() !== ""
        ? status
        : "";
  }
};

export const getAgendaToleranceStatusLabel = (toleranceStatus) => {
  switch (toleranceStatus) {
    case "WITHIN":
    case "EXCEEDS":
      return TOLERANCE_STATUS_LABELS[toleranceStatus];
    default:
      return typeof toleranceStatus === "string" &&
        toleranceStatus.trim() !== ""
        ? toleranceStatus
        : "";
  }
};

export const getAgendaSlotBadgeLabel = (slot) => {
  if (!slot?.isBookable) return "";
  if (!slot?.status || slot.status === "AVAILABLE") return "";
  return getAgendaSlotStatusLabel(slot.status);
};

export const buildAgendaHourOptions = (slots, shiftEnforcementMode) =>
  dedupeAgendaOptions(
    (slots ?? [])
      .map((slot) => normalizeAgendaSlot(slot))
      .filter(Boolean)
      .map((slot) => {
        const outOfShift =
          !slot.isBookable &&
          slot.isOccupied !== true &&
          shiftEnforcementMode === "OPEN";
        const isDisabled =
          slot.isOccupied === true ||
          (shiftEnforcementMode === "STRICT" && slot.isBookable !== true);

        return {
          ...slot,
          badgeLabel: getAgendaSlotBadgeLabel(slot),
          isDisabled,
          outOfShift,
        };
      })
  ).sort(compareAgendaOptionOrder);

export const getConsultas = async ({ ...parameters }) => {
  try {
    const apiUrl = buildUrl("/controller/agenda.php", "citas", parameters);
    const response = await servicios.get(apiUrl, { responseType: "json" });
    if (response.status >= 200 && response.status < 300) {
      return response?.data;
    }
  } catch (error) {
    throw error;
  }
};

export const getConsulta = async ({ ...parameters }) => {
  try {
    const apiUrl = buildUrl("/controller/agenda.php", "cita", parameters);
    const response = await servicios.get(apiUrl, { responseType: "json" });
    if (response.status >= 200 && response.status < 300) {
      return response?.data?.data ?? [];
    }
  } catch (error) {
    throw error;
  }
};

export const getConsultaArchivos = async ({ ...parameters }) => {
  try {
    const apiUrl = buildUrl("/controller/agenda.php", "archivos", parameters);
    const response = await servicios.get(apiUrl, { responseType: "json" });
    if (response.status >= 200 && response.status < 300) {
      return response?.data?.data ?? [];
    }
  } catch (error) {
    throw error;
  }
};

export const getConsultaArchivoDownload = async ({ ...parameters }) => {
  try {
    const apiUrl = buildUrl(
      "/controller/agenda.php",
      "archivoDescargar",
      parameters
    );
    const response = await servicios.get(apiUrl, {
      responseType: "blob",
    });
    if (response.status >= 200 && response.status < 300) {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", parameters.archivo);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  } catch (error) {
    throw error;
  }
};

export const getEstadosConsultaCantidadDia = async ({ ...parameters }) => {
  try {
    const apiUrl = buildUrl(
      "/controller/agenda.php",
      "consulta/estado",
      parameters
    );
    const response = await servicios.get(apiUrl, { responseType: "json" });
    if (response.status >= 200 && response.status < 300) {
      return response?.data?.data ?? [];
    }
  } catch (error) {
    throw error;
  }
};

const normalizeConsultaDia = (raw) => {
  const sourceTimezone = toNullableString(raw?.timezone);
  const timezone = getBrowserTimeZone();
  const slots = Array.isArray(raw?.slots)
    ? raw.slots
        .map((slot) => {
          if (!slot || typeof slot !== "object") return null;
          const startUtc = toNullableString(slot?.startUtc);
          const endUtc = toNullableString(slot?.endUtc);

          if (!timezone) {
            return buildInvalidAgendaSlot(
              slot,
              "No se pudo detectar la zona horaria del navegador."
            );
          }

          if (!startUtc || !endUtc) {
            return buildInvalidAgendaSlot(
              slot,
              "El slot no incluye fecha UTC completa para interpretarse en la zona horaria del navegador."
            );
          }

          const localStartDateTime = utcToLocal(startUtc, timezone);
          const localEndDateTime = utcToLocal(endUtc, timezone);

          if (!localStartDateTime || !localEndDateTime) {
            return buildInvalidAgendaSlot(
              slot,
              "No se pudo interpretar la hora del slot en la zona horaria del navegador."
            );
          }

          return normalizeAgendaSlot({
            startUtc,
            endUtc,
            localStartDateTime,
            localEndDateTime,
            isBookable: slot?.isBookable === true,
            unavailableMinutesInsideSlot:
              toFiniteOrNull(slot?.unavailableMinutesInsideSlot) ?? 0,
            unavailablePercentInsideSlot:
              toFiniteOrNull(slot?.unavailablePercentInsideSlot) ?? 0,
            status: toNullableString(slot?.status),
            toleranceStatus: toNullableString(slot?.toleranceStatus),
            citaId: toFiniteOrNull(slot?.citaId),
            timezone,
          });
        })
        .filter(Boolean)
    : [];

  return {
    mode: toNullableString(raw?.mode) ?? "CLASSIC",
    shiftEnforcementMode: toNullableString(raw?.shiftEnforcementMode),
    userId: toFiniteOrNull(raw?.userId),
    serviceId: toFiniteOrNull(raw?.serviceId),
    areaId: toFiniteOrNull(raw?.areaId),
    centerId: toFiniteOrNull(raw?.centerId),
    timezone,
    sourceTimezone,
    dateLocal: toNullableString(raw?.dateLocal),
    centerSchedule: raw?.centerSchedule
      ? {
          slotMinutes: toFiniteOrNull(raw?.centerSchedule?.slotMinutes),
          unavailableTolerancePercent: toNullableString(
            raw?.centerSchedule?.unavailableTolerancePercent
          ),
        }
      : null,
    conflictSummary: normalizeConflictSummary(raw?.conflictSummary),
    slots,
  };
};

export const getConsultaDia = async ({ ...parameters }) => {
  try {
    const apiUrl = buildUrl(
      "/controller/agendaScheduling.php",
      "agendaSlotsForDayView",
      toAgendaQueryParameters(parameters)
    );
    const response = await servicios.get(apiUrl, { responseType: "json" });
    if (response.status >= 200 && response.status < 300) {
      return normalizeConsultaDia(response?.data?.data ?? response?.data);
    }
    return normalizeConsultaDia(null);
  } catch (error) {
    throw error;
  }
};

const normalizeDiasDisponibles = (raw) => {
  const days = Array.isArray(raw?.days) ? raw.days : [];
  const normalizedDays = days
    .map((day) => {
      if (!day || typeof day !== "object") return null;
      return {
        dateLocal: toNullableString(day?.dateLocal),
        hasBookableSlot: day?.hasBookableSlot === true,
        slotCount: toFiniteOrNull(day?.slotCount),
        availableMinutes: toFiniteOrNull(day?.availableMinutes),
        unavailableMinutes: toFiniteOrNull(day?.unavailableMinutes),
        conflictSummary: normalizeConflictSummary(day?.conflictSummary, {
          includeReasons: true,
        }),
      };
    })
    .filter(Boolean);

  return {
    mode: toNullableString(raw?.mode) ?? "CLASSIC",
    shiftEnforcementMode: toNullableString(raw?.shiftEnforcementMode),
    userId: toFiniteOrNull(raw?.userId),
    serviceId: toFiniteOrNull(raw?.serviceId),
    areaId: toFiniteOrNull(raw?.areaId),
    centerId: toFiniteOrNull(raw?.centerId),
    timezone: toNullableString(raw?.timezone),
    slotMinutes: toFiniteOrNull(raw?.slotMinutes),
    rangeStartUtc: toNullableString(raw?.rangeStartUtc),
    rangeEndUtc: toNullableString(raw?.rangeEndUtc),
    days: normalizedDays,
  };
};

export const getAgendaDayDate = (day) => day?.dateLocal ?? null;

export const isAgendaDayBookable = (day) => day?.hasBookableSlot === true;

export const isAgendaOpenScheduling = (availability) =>
  availability?.mode === "SCHEDULING" &&
  availability?.shiftEnforcementMode === "OPEN";

export const hasAgendaBookableDays = (availability) =>
  (availability?.days ?? []).some((day) => isAgendaDayBookable(day));

export const shouldAgendaDayLookOutOfShift = (availability, day) => {
  if (availability?.mode !== "SCHEDULING") return false;
  if (isAgendaDayBookable(day)) return false;
  if (isAgendaOpenScheduling(availability)) return true;
  return day != null;
};

export const describeAgendaDay = (day) => {
  if (!day || typeof day !== "object") return "";

  const summary = [];
  if (day.hasBookableSlot) {
    summary.push(
      Number.isFinite(day.slotCount) ? `${day.slotCount} slots` : "Agendable"
    );
  } else {
    summary.push("Sin slots agendables");
  }

  if (Number.isFinite(day.availableMinutes)) {
    summary.push(`${day.availableMinutes} min disponibles`);
  }

  if (Number.isFinite(day.unavailableMinutes) && day.unavailableMinutes > 0) {
    summary.push(`${day.unavailableMinutes} min no disponibles`);
  }

  return summary.join(" | ");
};

export const getDiasDisponibles = async (parameters = {}) => {
  const apiUrl = buildUrl(
    "/controller/agendaScheduling.php",
    "agendaDaysForRangeView",
    toAgendaQueryParameters(parameters)
  );
  const response = await servicios.get(apiUrl, { responseType: "json" });
  if (response.status >= 200 && response.status < 300) {
    return normalizeDiasDisponibles(response?.data?.data ?? response?.data);
  }
  return normalizeDiasDisponibles(null);
};

export const getConsultaIniciada = async ({ ...parameters }) => {
  try {
    const apiUrl = buildUrl(
      "/controller/agenda.php",
      "citaIniciada",
      parameters
    );
    const response = await servicios.get(apiUrl, { responseType: "json" });
    if (response.status >= 200 && response.status < 300) {
      if (response?.data?.rsp != null && Number(response.data.rsp) !== 1) {
        throw new Error(response.data.msg || "No se pudo iniciar la consulta");
      }
      return response?.data?.data ?? {};
    }
  } catch (error) {
    throw error;
  }
};

export const getProcedimientos = async ({ ...parameters }) => {
  try {
    const apiUrl = buildUrl(
      "/controller/combos.php",
      "procedimientos",
      parameters
    );
    const response = await servicios.get(apiUrl, { responseType: "json" });
    if (response.status >= 200 && response.status < 300) {
      return response?.data?.data ?? [];
    }
  } catch (error) {
    throw error;
  }
};

export const getCentros = async (idusuario) => {
  try {
    const apiUrl = buildUrl("/controller/combos.php", "centrosusuarios");
    const response = await servicios.get(apiUrl, {
      responseType: "json",
      params: { idusuario },
    });
    if (response.status >= 200 && response.status < 300) {
      return response?.data?.data ?? [];
    }
  } catch (error) {
    throw error;
  }
};

export const saveCentro = (form) =>
  servicios.post("controller/usuarios.php", form, {
    responseType: "json",
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

export const getDiagnosticos = async ({ ...parameters }) => {
  try {
    const apiUrl = buildUrl(
      "/controller/combos.php",
      "diagnosticos",
      parameters
    );
    const response = await servicios.get(apiUrl, { responseType: "json" });
    if (response.status >= 200 && response.status < 300) {
      return response?.data?.data ?? [];
    }
  } catch (error) {
    throw error;
  }
};

export const getFavoritos = async ({ ...parameters }) => {
  try {
    const apiUrl = buildUrl("/controller/agenda.php", "favoritos", parameters);
    const response = await servicios.get(apiUrl, { responseType: "json" });
    if (response.status >= 200 && response.status < 300) {
      return response?.data?.data ?? [];
    }
  } catch (error) {
    throw error;
  }
};

export const getFavoritosProcedimientos = async ({ ...parameters }) => {
  try {
    const apiUrl = buildUrl(
      "/controller/agenda.php",
      "favoritosProcedimientos",
      parameters
    );
    const response = await servicios.get(apiUrl, { responseType: "json" });
    if (response.status >= 200 && response.status < 300) {
      return response?.data?.data ?? [];
    }
  } catch (error) {
    throw error;
  }
};

export const postAgenda = (form) =>
  servicios.post("controller/agenda.php", form, {
    responseType: "json",
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
